import { useEffect, useState, useCallback, useRef } from "react";
import { useDebouncedValue } from "../lib/useDebounce";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Save, FileText } from "lucide-react";
import api from "../lib/api";

const ORDER_STATUSES = ["ORDERED", "SUPPLIER_CONFIRMED", "IN_PRODUCTION", "READY_TO_SHIP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"];
const STATUS_COLORS = {
  ORDERED: "bg-blue-100 text-blue-700", SUPPLIER_CONFIRMED: "bg-purple-100 text-purple-700", IN_PRODUCTION: "bg-amber-100 text-amber-700",
  READY_TO_SHIP: "bg-amber-100 text-amber-700", IN_TRANSIT: "bg-sky-100 text-sky-700",
  OUT_FOR_DELIVERY: "bg-orange-100 text-orange-700", DELIVERED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-600",
};
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Orders() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const dq = useDebouncedValue(q);
  const seq = useRef(0);
  const load = useCallback(() => {
    const s = ++seq.current;
    setError(false);
    api.get("/admin/orders", { params: { status: status || undefined, q: dq || undefined } })
      .then((r) => { if (s === seq.current) setItems(r.data); })
      .catch(() => { if (s === seq.current) { setItems([]); setError(true); } });
  }, [status, dq]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-orders-page">
      <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Purchase Orders &amp; Shipments</h1>
      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <input data-testid="orders-search-input" className="pg-input !py-2.5 w-[260px]" placeholder="Search PO #, customer, tracking..." value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="orders-status-filter" className="pg-input !py-2.5 w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      {error && (
        <div className="mt-5 bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="orders-error-state">
          <p className="text-red-600 text-[14px]">Failed to load orders.</p>
          <button data-testid="orders-retry-button" onClick={load} className="btn-primary mt-3 !py-2 !px-4 text-[12.5px]">Retry</button>
        </div>
      )}
      {!error && items.length === 0 && (
        <div className="mt-5 bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="orders-empty-state">
          No orders yet. Orders are created automatically when a customer quote is accepted.
        </div>
      )}

      <div className="mt-5 space-y-3">
        {items.map((o) => (
          <div key={o.id} className="bg-white rounded-xl border border-slate-200" data-testid={`order-row-${o.order_number}`}>
            <div className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-[220px]">
                <div className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">{o.order_number}
                  <span className={`ml-3 px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${STATUS_COLORS[o.status] || "bg-slate-100"}`}>{o.status.replace(/_/g, " ")}</span>
                </div>
                <div className="text-[12.5px] text-slate-500 mt-1">
                  {o.customer_company || o.customer_name} · {o.product} · {money(o.total)} · from {o.quote_number}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
                {o.tracking_number && <span>{o.carrier} · {o.tracking_number}</span>}
                <button data-testid={`order-expand-${o.id}`} onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  className="text-slate-400 hover:text-[var(--pg-navy)] p-1.5" aria-label="Manage order">
                  {expanded === o.id ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
                </button>
              </div>
            </div>
            {expanded === o.id && <OrderEditor order={o} onSaved={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderEditor({ order, onSaved }) {
  const [f, setF] = useState({
    status: order.status, carrier: order.carrier || "", tracking_number: order.tracking_number || "",
    estimated_delivery: order.estimated_delivery || "", note: "",
  });
  const [saving, setSaving] = useState(false);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, f);
      toast.success("Order updated" + (f.status !== order.status && ["IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"].includes(f.status) ? " — customer notified" : ""));
      onSaved();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t border-slate-100 p-5 bg-slate-50 rounded-b-xl" data-testid={`order-editor-${order.id}`}>
      <div className="grid sm:grid-cols-5 gap-3">
        <div>
          <label className="pg-label">Status</label>
          <select data-testid="order-status-select" className="pg-input !py-2.5" value={f.status} onChange={h("status")}>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div><label className="pg-label">Carrier</label><input data-testid="order-carrier-input" className="pg-input !py-2.5" value={f.carrier} onChange={h("carrier")} placeholder="e.g. FedEx Freight" /></div>
        <div><label className="pg-label">Tracking #</label><input data-testid="order-tracking-input" className="pg-input !py-2.5" value={f.tracking_number} onChange={h("tracking_number")} /></div>
        <div><label className="pg-label">Est. Delivery</label><input data-testid="order-eta-input" type="date" className="pg-input !py-2.5" value={f.estimated_delivery} onChange={h("estimated_delivery")} /></div>
        <div><label className="pg-label">Status Note</label><input data-testid="order-note-input" className="pg-input !py-2.5" value={f.note} onChange={h("note")} placeholder="Optional" /></div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button data-testid="order-save-button" onClick={save} disabled={saving} className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">
          <Save size={14} /> {saving ? "Saving..." : "Save & Notify"}
        </button>
        <button data-testid="order-generate-invoice-button" onClick={async () => {
          try {
            const { data } = await api.post(`/admin/orders/${order.id}/invoice`);
            toast.success(`Invoice ${data.invoice_number} ready — see Invoices tab`);
          } catch { toast.error("Invoice generation failed"); }
        }} className="inline-flex items-center gap-1.5 text-[13px] font-[Poppins] font-semibold px-4 py-2.5 rounded-full bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] transition-colors">
          <FileText size={14} /> Generate Invoice
        </button>
      </div>

      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold mb-2">Timeline</div>
        <ul className="space-y-1.5">
          {[...(order.timeline || [])].reverse().map((t, i) => (
            <li key={`${t.status}-${t.at}-${i}`} className="text-[12.5px] text-slate-600">
              <strong className="text-[var(--pg-navy)]">{t.status.replace(/_/g, " ")}</strong> · {(t.at || "").slice(0, 16).replace("T", " ")}{t.note && ` — ${t.note}`}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
